import { useCallback, useLayoutEffect, useState, useEffect, useRef } from "react";
import { usePageTitle } from "../../../hooks/usePageTitle";
import useFitRows from "../../../hooks/useFitRows";
import useServerTable from "../../../hooks/useServerTable";
import {
  User,
  TrendingUp,
  Users,
  BanknoteArrowDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import DashboardLayout from "../../../layouts/DashboardLayout/DashboardLayout";
import StatCard from "../../../components/StatCard/StatCard";
import PageFooter from "../../../components/PageFooter/PageFooter";
import Pagination from "../../../components/Pagination/Pagination";
import SearchInput from "../../../components/SearchInput/SearchInput";
import styles from "./Leaderboard.module.css";

/* The endpoint predates the shared {items,total,totalPages} envelope and is
   consumed elsewhere, so it keeps its own names and the hook adapts. */
const LEADERBOARD_QUERY = { sort: "sortBy", dir: "sortDir" };
const selectBoard = (data) => ({
  items: data?.traders,
  total: data?.totalTraders,
  totalPages: data?.totalPages,
});

const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };
// Starting point only — the real count is measured from the window height so
// the table ends where the screen does. See useFitRows.
const TRADERS_PAGE_SIZE = 12;
const TRADERS_MIN_ROWS = 6;
const TRADERS_MAX_ROWS = 30;

/* ── Formatting helpers ── */
const money0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString("en-US")}`;
const compact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;
  return money0(v);
};
const pctLabel = (n) => (n == null ? "-" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(1)}%`);

// Slop, so a scroller resting a pixel off the end doesn't show a fade.
const EDGE_SLOP = 4;

/* A trader is identified by their initials — "Alex Barry" is "AB" — which the API
   sends as `username`. It arrives ready to print, so there is nothing to derive
   here; this only guards a row the API had no name to build from. */
const username = (trader) => trader?.username || "-";

function Leaderboard() {
  usePageTitle("Leaderboard");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ col: null, dir: "desc" });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 767,
  );

  /* Track mobile breakpoint (matches the 767px CSS breakpoint) */
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Searching and sorting happen on the server: the client only holds one
  // page, so doing either here would silently apply to that page alone.
  const handleSort = (col) => {
    setSort((s) =>
      s.col === col
        ? { col, dir: s.dir === "desc" ? "asc" : "desc" }
        : { col, dir: "desc" },
    );
  };

  // Rows are sized to the viewport on desktop; mobile renders the whole list
  // with no pagination, so there is nothing to fit there.
  const paginationRef = useRef(null);
  const footerRef = useRef(null);
  const { bodyRef, rowsPerPage } = useFitRows({
    fallback: TRADERS_PAGE_SIZE,
    belowRefs: [paginationRef, footerRef],
    min: TRADERS_MIN_ROWS,
    max: TRADERS_MAX_ROWS,
    gutter: 24, // .content bottom padding
    enabled: !isMobile,
  });

  const {
    rows: pageRows,
    data: board,
    page: currentPage,
    setPage,
    totalPages,
    loading,
    error,
  } = useServerTable({
    url: "/leaderboard",
    limit: rowsPerPage,
    search,
    sort,
    query: LEADERBOARD_QUERY,
    select: selectBoard,
    // Mobile scrolls the whole board rather than paging it, and the endpoint
    // returns everything when neither page nor limit is sent.
    paged: !isMobile,
  });

  // Only meaningful on mobile, where the list is unpaged and these really are
  // the platform's top three. Desktop hides the podium in CSS.
  const top3 = pageRows.slice(0, 3);
  const showPodium = !search && top3.length === 3;

  const yourRank = board?.yourRank;
  const rankChange = board?.rankChange;

  /* Mobile scrolls the whole board inside the table, so the edges need to say
     whether there's more above or below. Header height is measured rather
     than assumed — it's sticky, and the top fade belongs under it. */
  const scrollerRef = useRef(null);
  const headRef = useRef(null);
  const [edges, setEdges] = useState({ top: false, bottom: false, headH: 0 });

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const room = el.scrollHeight - el.clientHeight;
    setEdges((prev) => {
      const next = {
        top: el.scrollTop > EDGE_SLOP,
        bottom: room > EDGE_SLOP && el.scrollTop < room - EDGE_SLOP,
        headH: headRef.current?.offsetHeight ?? 0,
      };
      return prev.top === next.top && prev.bottom === next.bottom && prev.headH === next.headH
        ? prev
        : next;
    });
  }, []);

  // After every render: a new page of rows changes how much there is to scroll.
  useLayoutEffect(updateEdges);

  useEffect(() => {
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [updateEdges]);

  return (
    <DashboardLayout>
      <div className={styles.leaderboardPage}>
        <h1 className={styles.pageTitle}>Leaderboard</h1>
        <p className={styles.pageSubtitle}>
          Track the performance of every trader on the platform.
        </p>

        <div className={`row g-3 mb-4 ${styles.statsRow}`}>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard
              label="Your Rank"
              value={yourRank != null ? `#${yourRank}` : "Unranked"}
              icon={User}
              info
              tooltip={{
                text: "Ranking is based on Total AUM. Sorting by net withdrawal or return on fees does not change your rank. You appear on the board once you hold an Instant Funding (DFY) account, whether or not it has been deployed yet.",
              }}
              subValue={
                rankChange != null ? (
                  <>
                    {rankChange >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />} {Math.abs(rankChange)} this month
                  </>
                ) : undefined
              }
            />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="Total AUM" value={board ? compact(board.totalAum) : "-"} icon={TrendingUp} />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard label="Active Accounts" value={board ? String(board.activeAccounts ?? 0) : "-"} icon={Users} />
          </div>
          <div className={`col-md-3 col-12 ${styles.statsCol}`}>
            <StatCard
              label="Net Withdrawals"
              value={board ? money0(board.netWithdrawals) : "-"}
              icon={BanknoteArrowDown}
            />
          </div>
        </div>

        <div
          className={`${styles.tableHeader} d-flex align-items-center justify-content-between`}
        >
          <h2 className={styles.sectionTitle}>Traders</h2>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search username"
            className={styles.searchWrapper}
          />
        </div>

        <div className={styles.tableSection}>
          {/* Mobile podium: top 3 traders (hidden on desktop, hidden when searching) */}
          {showPodium && (
            <div className={styles.podium}>
              <div className={`${styles.podiumItem} ${styles.podiumSecond}`}>
                <div className={styles.avatarWrap}>
                  <div className={styles.avatar}>
                    {username(top3[1])}
                  </div>
                  <span className={styles.medalBadge}>🥈</span>
                </div>
                <span className={styles.podiumName}>{username(top3[1])}</span>
                <span className={styles.podiumValue}>
                  {money0(top3[1].netWithdrawals)}
                </span>
              </div>
              <div className={`${styles.podiumItem} ${styles.podiumFirst}`}>
                <div className={styles.avatarWrap}>
                  <div className={styles.avatar}>
                    {username(top3[0])}
                  </div>
                  <span className={styles.medalBadge}>🥇</span>
                </div>
                <span className={styles.podiumName}>{username(top3[0])}</span>
                <span className={styles.podiumValue}>
                  {money0(top3[0].netWithdrawals)}
                </span>
              </div>
              <div className={`${styles.podiumItem} ${styles.podiumThird}`}>
                <div className={styles.avatarWrap}>
                  <div className={styles.avatar}>
                    {username(top3[2])}
                  </div>
                  <span className={styles.medalBadge}>🥉</span>
                </div>
                <span className={styles.podiumName}>{username(top3[2])}</span>
                <span className={styles.podiumValue}>
                  {money0(top3[2].netWithdrawals)}
                </span>
              </div>
            </div>
          )}

          <div className={styles.scrollArea}>
            <span
              className={`${styles.fade} ${styles.fadeTop}${edges.top ? ` ${styles.fadeOn}` : ""}`}
              style={{ top: edges.headH }}
              aria-hidden="true"
            />
            <span
              className={`${styles.fade} ${styles.fadeBottom}${edges.bottom ? ` ${styles.fadeOn}` : ""}`}
              aria-hidden="true"
            />
            <div className={styles.tableWrapper} ref={scrollerRef} onScroll={updateEdges}>
            <table className={styles.table}>
              <thead ref={headRef}>
                <tr>
                  <th>Rank</th>
                  <th>Username</th>
                  <th className={styles.mobileHide}>Total AUM</th>
                  <th className={styles.mobileHide}>Active accounts</th>
                  <th>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSort("netWithdrawals")}
                    >
                      Net withdrawals
                      {sort.col === "netWithdrawals" && sort.dir === "asc" ? (
                        <ArrowUp
                          size={12}
                          strokeWidth={3}
                          className={styles.sortIcon}
                        />
                      ) : (
                        <ArrowDown
                          size={12}
                          strokeWidth={3}
                          className={styles.sortIcon}
                        />
                      )}
                    </button>
                  </th>
                  <th className={styles.mobileHide}>
                    <button
                      className={styles.thBtn}
                      onClick={() => handleSort("returnOnFees")}
                    >
                      Return on fees
                      {sort.col === "returnOnFees" && sort.dir === "asc" ? (
                        <ArrowUp size={12} />
                      ) : (
                        <ArrowDown size={12} />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {loading && pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      Loading traders…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      {error}
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      {search ? <>No results found for &ldquo;{search}&rdquo;</> : "No traders yet"}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((trader) => (
                    <tr
                      key={trader.userId}
                      className={[
                        trader.isCurrentUser ? styles.currentUserRow : "",
                        !search && trader.rank <= 3 ? styles.mobileTopHide : "",
                      ].join(" ")}
                    >
                      <td>
                        <span className={styles.rankMedal}>
                          {MEDALS[trader.rank] && (
                            <span>{MEDALS[trader.rank]}</span>
                          )}
                          <span
                            className={trader.rank <= 3 ? styles.rankTop : ""}
                          >
                            {trader.rank}
                          </span>
                        </span>
                      </td>
                      <td>{username(trader)}</td>
                      <td className={styles.mobileHide}>{compact(trader.totalAum)}</td>
                      <td className={styles.mobileHide}>
                        {trader.activeAccounts}
                      </td>
                      <td>{money0(trader.netWithdrawals)}</td>
                      <td
                        className={`${styles.mobileHide} ${styles.returnPositive}`}
                      >
                        {pctLabel(trader.returnOnFees)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>

          {!isMobile && (
            <div className={styles.paginationWrap} ref={paginationRef}>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
                variant="outline"
              />
            </div>
          )}
        </div>

        <PageFooter ref={footerRef} />
      </div>
    </DashboardLayout>
  );
}

export default Leaderboard;
